/* eslint-disable no-nested-ternary */
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import classNames from 'classnames';

import AdminService from '../../services/AdminService';
import UserService from '../../services/UserService';
import { UiContext } from '../../context/UiContext';
import { hasAuthData } from '../../helpers/authStorage';

import AutoButton from '../../components/AutoButton';
import SegmentedControl from '../../components/SegmentedControl';
import MultiDropdown from '../../components/Form/MultiDropdown';
import SelectedUsers from '../../components/Dashboard/SelectedUsers';
import Textarea from '../../components/Form/Textarea';

import classes from './styles.module.scss';
import { useUserStore } from '../../store';

// List of common timezones
const TIMEZONES = [
  'Pacific/Midway',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Caracas',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Prague',
  'Europe/Athens',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

// Helper to format date as "Mon Apr 24"
function formatDateHeader(date) {
  const d = new Date(date);
  const month = d.toLocaleString('en-us', { month: 'short' });
  const day = d.getDate();
  const dayOfWeek = d.toLocaleString('en-us', { weekday: 'short' });
  return { month, day, dayOfWeek, dateString: `${month} ${day}` };
}

// Generate time slots (30 min intervals from 8:00 AM to 8:00 PM)
function generateTimeSlots() {
  const slots = [];
  const startHour = 8;
  const endHour = 20; // 8 PM in 24-hour format

  for (let hour = startHour; hour <= endHour; hour += 1) {
    for (let minute = 0; minute < 60; minute += 30) {
      if (hour === endHour && minute > 0) break; // Stop at 8:00 PM
      const time24 = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const time12 = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
      slots.push({ time24, time12 });
    }
  }
  return slots;
}

// Generate next 10 days starting from today
function generateWeekdays() {
  const days = [];
  const today = new Date();

  for (let i = 0; i < 10; i += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    days.push(date);
  }
  return days;
}

export default function InterviewSlotModal({
  show,
  handleClose,
  jobApplication,
  refetch,
}) {
  const { t } = useTranslation();
  const { showNotification, setIsFetching, showModal } = useContext(UiContext);
  const isAuthorized = hasAuthData();

  const [interviewType, setInterviewType] = useState({
    value: 'Video',
    label: t('modals.InterviewSlotModal.video'),
  });
  const [provideLater, setProvideLater] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');
  const companyId = useUserStore((state) => state.companyId);
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const { data: job } = useQuery({
    queryKey: ['myJob', jobApplication?.jobPostId],
    queryFn: () => AdminService.getJobPost({ id: jobApplication?.jobPostId }),
    enabled: !!jobApplication?.jobPostId && show,
  });

  const { data: companyManagers } = useQuery({
    queryKey: ['companyManagers', companyId],
    queryFn: () => AdminService.getCompanyManagers({ id: companyId }),
    enabled: !!companyId && show,
  });

  // Set timezone from user profile when data is loaded
  useEffect(() => {
    if (me?.userProfile?.timezone) {
      setTimezone(me.userProfile.timezone);
    }
  }, [me]);

  // Preselect current user from company managers when modal opens
  useEffect(() => {
    if (show && me && companyManagers) {
      const currentManager = companyManagers?.find(
        (manager) => manager.userId === me.id
      );
      if (currentManager) {
        setInterviewers([
          {
            label: `${currentManager.firstName} ${currentManager.lastName}`,
            value: currentManager.id,
            originalData: currentManager,
          },
        ]);
      }
    }
  }, [show, me, companyManagers]);

  const timeSlots = useMemo(() => generateTimeSlots(), []);
  const weekdays = useMemo(() => generateWeekdays(), []);

  const interviewTypeOptions = useMemo(
    () => [
      { value: 'Video', label: t('modals.InterviewSlotModal.video') },
      { value: 'InPerson', label: t('modals.InterviewSlotModal.inPerson') },
      { value: 'Phone', label: t('modals.InterviewSlotModal.phone') },
    ],
    [t]
  );

  const handleSlotClick = useCallback(
    (day, slot) => {
      if (provideLater) return;

      const slotKey = `${day.toISOString()}_${slot.time24}`;

      setSelectedSlots((prev) => {
        const exists = prev.find((s) => s.key === slotKey);
        if (exists) {
          return prev.filter((s) => s.key !== slotKey);
        }
        if (prev.length >= 3) {
          showNotification({
            text: t('modals.InterviewSlotModal.maxSlotsReached'),
          });
          return prev;
        }
        return [...prev, { key: slotKey, day, slot }];
      });
      setHasChanges(true);
    },
    [provideLater, showNotification, t]
  );

  const isSlotSelected = useCallback(
    (day, slot) => {
      const slotKey = `${day.toISOString()}_${slot.time24}`;
      return selectedSlots.some((s) => s.key === slotKey);
    },
    [selectedSlots]
  );

  const isSlotDisabled = useCallback((day, slot) => {
    // Check if slot is in the past
    const now = new Date();
    const slotDate = new Date(day);
    const [hours, minutes] = slot.time24.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);

    return slotDate < now;
  }, []);

  const handleClose_ = useCallback(() => {
    if (hasChanges) {
      showModal({
        title: t('modals.InterviewSlotModal.discardChanges'),
        text: t('modals.InterviewSlotModal.discardChangesText'),
        confirmButtonLabel: t('common.yes'),
        dismissButtonLabel: t('common.no'),
        onConfirm: () => {
          setHasChanges(false);
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose, showModal, t]);

  const handleSubmit = async () => {
    try {
      setIsFetching(true);

      // Map interview type: Phone -> Audio for backend
      const backendType =
        interviewType.value === 'Phone' ? 'Audio' : interviewType.value;

      // Convert selected slots to backend format
      const slotDates = selectedSlots.map((s) => {
        const slotDate = new Date(s.day);
        const [hours, minutes] = s.slot.time24.split(':').map(Number);
        slotDate.setHours(hours, minutes, 0, 0);
        return {
          startDateTime: slotDate.toISOString(),
        };
      });

      // Extract company manager IDs
      const companyManagerIds = interviewers.map((i) => i.value);

      await AdminService.createJobApplicationInterviewSlot({
        jobApplicationId: jobApplication.id,
        type: backendType,
        note: notes || null,
        customLocation: null,
        connectionDetail: null,
        companyManagerIds,
        slotDates: provideLater ? [] : slotDates,
      });

      handleClose();
      refetch();
      showNotification({
        text: t('modals.InterviewSlotModal.requestSent'),
      });
    } catch (error) {
      showModal({
        title: t('modals.InterviewSlotModal.error'),
        text: t('modals.InterviewSlotModal.errorMessage'),
      });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!show) {
      // Reset form when modal closes
      setInterviewType({
        value: 'Video',
        label: t('modals.InterviewSlotModal.video'),
      });
      setProvideLater(false);
      setSelectedSlots([]);
      setInterviewers([]);
      setNotes('');
      setHasChanges(false);
    }
  }, [show, t]);

  const isDisabled = useMemo(() => {
    // Interview type must be selected
    if (!interviewType) return true;

    // At least 1 panel member (including submitter)
    if (interviewers.length === 0) return true;

    // Either slots selected OR "provide later" checked
    if (!provideLater && selectedSlots.length === 0) return true;

    return false;
  }, [interviewType, interviewers.length, provideLater, selectedSlots.length]);

  return (
    <Modal
      show={show}
      onHide={handleClose_}
      centered
      className={classes.InterviewSlotModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header>
          <h1>{t('modals.InterviewSlotModal.title')}</h1>
          <i className={classes.closeIcon} onClick={handleClose_}>
            Close
          </i>
        </header>

        <div className={classes.container}>
          {/* Candidate Header Row */}
          <div className={classes.candidateRow}>
            <div className={classes.candidateInfo}>
              <div className={classes.candidateNameRow}>
                <h2>
                  {jobApplication?.user?.userProfile?.firstName}{' '}
                  {jobApplication?.user?.userProfile?.lastName}
                </h2>
              </div>
              <p className={classes.jobInfo}>
                {job?.jobTitle}. {t('modals.InterviewSlotModal.req')} #
                {jobApplication?.jobPostId}
              </p>
            </div>
          </div>

          {/* Interview Type */}
          <div className={classes.row}>
            <div className={classes.label}>
              {t('modals.InterviewSlotModal.interviewType')}
            </div>
            <div className={classes.segmentedWrapper}>
              <SegmentedControl
                ariaLabel={t('modals.InterviewSlotModal.interviewType')}
                items={interviewTypeOptions}
                selectedItem={interviewType}
                onChange={(val) => {
                  setInterviewType(val);
                  setHasChanges(true);
                }}
              />
            </div>
          </div>

          {/* Availability Section */}
          <div className={classes.row}>
            <div className={classes.label}>
              {t('modals.InterviewSlotModal.availability')}
            </div>
            <div className={classes.checkboxWrapper}>
              <input
                type="checkbox"
                id="provideLater"
                checked={provideLater}
                onChange={(e) => {
                  setProvideLater(e.target.checked);
                  if (e.target.checked) {
                    setSelectedSlots([]);
                  }
                  setHasChanges(true);
                }}
              />
              <label htmlFor="provideLater">
                {t('modals.InterviewSlotModal.provideLater')}
              </label>
            </div>
          </div>

          {/* Availability Grid and Interview Panel Side by Side */}
          <div className={classes.mainRow}>
            {/* Availability Grid */}
            <div className={classes.availabilitySection}>
              <div className={classes.gridHeader}>
                <h3>{t('modals.InterviewSlotModal.pickSlots')}</h3>
                <div className={classes.timezoneSelector}>
                  <span>{t('modals.InterviewSlotModal.timezone')}:</span>
                  <select
                    value={timezone}
                    onChange={(e) => {
                      setTimezone(e.target.value);
                      setHasChanges(true);
                    }}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={classes.calendar}>
                <table className={classes.calendarTable}>
                  <thead>
                    <tr>
                      <th className={classes.timeHeader}>
                        {t('modals.InterviewSlotModal.time')}
                      </th>
                      {weekdays.map((day) => {
                        const formatted = formatDateHeader(day);
                        return (
                          <th
                            key={day.toISOString()}
                            className={classes.dayHeader}
                          >
                            <div className={classes.dayHeaderContent}>
                              <div className={classes.date}>
                                {formatted.dateString}
                              </div>
                              <div className={classes.dayOfWeek}>
                                {formatted.dayOfWeek}
                              </div>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((slot) => (
                      <tr key={slot.time24}>
                        <td className={classes.timeCell}>{slot.time12}</td>
                        {weekdays.map((day) => {
                          const selected = isSlotSelected(day, slot);
                          const disabled =
                            isSlotDisabled(day, slot) || provideLater;

                          return (
                            <td
                              key={`${day.toISOString()}_${slot.time24}`}
                              className={classNames(classes.slotCell, {
                                [classes.selected]: selected,
                                [classes.disabled]: disabled,
                              })}
                              onClick={() =>
                                !disabled && handleSlotClick(day, slot)
                              }
                            >
                              {selected ? (
                                <i className={classes.checkIcon}>✓</i>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Selected Slots Display */}
              {selectedSlots.length > 0 && (
                <div className={classes.selectedSlots}>
                  <span className={classes.selectedLabel}>
                    {t('modals.InterviewSlotModal.selectedSlots')}:
                  </span>
                  {selectedSlots.map((s, idx) => {
                    const formatted = formatDateHeader(s.day);
                    return (
                      <span key={s.key}>
                        {formatted.dateString} {s.slot.time12}
                        {idx < selectedSlots.length - 1 && ' | '}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={classes.panelSection}>
            <MultiDropdown
              maxLength={5}
              note={t('modals.InterviewSlotModal.selectUpTo5')}
              width={350}
              label={t('modals.InterviewSlotModal.interviewPanel')}
              fetchOptions={{
                dataName: 'companyOptionPostJobSuccess',
                getDataHandler: AdminService.getAllManagers,
                fetchHanlderOptions: {
                  companyId,
                },
                isExternalUserList: true,
              }}
              value={interviewers}
              name="interviewers"
              setFieldValue={(name, value) => {
                setInterviewers(value);
                setHasChanges(true);
              }}
              placeholder={t('modals.InterviewSlotModal.selectInterviewer')}
            />
            <SelectedUsers
              style={{ width: 350 }}
              invitedUsers={interviewers}
              isExternalUserList
              setInvitedUsers={(users) => {
                setInterviewers(users);
                setHasChanges(true);
              }}
            />
          </div>

          <Textarea
            name="notes"
            width="100%"
            onChange={(e) => {
              setNotes(e.target.value);
              setHasChanges(true);
            }}
            value={notes}
            height={80}
            placeholder={t('modals.InterviewSlotModal.notesLabel')}
            maxLength={5000}
          />
        </div>

        {/* Action Buttons */}
        <footer>
          <AutoButton variant="black" onClick={handleClose_}>
            {t('modals.InterviewSlotModal.cancel')}
          </AutoButton>
          <AutoButton
            variant="submit"
            disabled={isDisabled}
            onClick={handleSubmit}
          >
            {provideLater
              ? t('modals.InterviewSlotModal.notifyRecruiterToProvide')
              : t('modals.InterviewSlotModal.confirmAndNotify')}
          </AutoButton>
        </footer>
      </div>
    </Modal>
  );
}
